import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Hidden from '@material-ui/core/Hidden';
import { makeStyles, Theme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import clsx from 'clsx';
import { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { EXTRA_PRODUCT_SPECS_BY_CODE } from 'src/constants/product';
import { useQuotationContext } from '../contexts/QuotationContext';
import { Product } from '../graphql';
import { DARK, GRAPHITE, ROSE, WHITE } from '../helpers/colors';
import { useQuotation } from '../helpers';
import useNumberCountUp from '../hooks/useNumberCountUp';

type Props = {
  product: Product;
  selected: boolean;
  onClick: (product: Product) => void;
};

const useStyles = makeStyles<Theme, { selected: boolean; discounted: boolean }>(
  theme => ({
    root: {
      padding: theme.spacing(1.5),
    },
    card: {
      position: 'relative',
      borderRadius: theme.shape.borderRadius * 2,
      cursor: 'pointer',
      height: '100%',
      backgroundColor: ({ selected }) => (selected ? '#FFE6F0' : 'transparent'),
      border: ({ selected }) =>
        selected ? 'solid 1.5px #FF0068' : 'solid 1.5px #dcdcdc',
    },
    activeOpacity: {
      opacity: ({ selected }) => (selected ? 1 : 0.6),
    },
    container: {
      width: 'unset',
      padding: ({ discounted }) =>
        discounted ? theme.spacing(2, 2) : theme.spacing(2.5, 2),
      [theme.breakpoints.up('sm')]: {
        padding: theme.spacing(2, 2),
        paddingBottom: theme.spacing(1),
      },
      alignItems: 'stretch',
      height: '100%',
    },
    priceContainer: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'flex-start',
      [theme.breakpoints.down('xs')]: {
        alignItems: ({ discounted }) => (discounted ? 'center' : 'flex-end'),
      },
    },
    productNameContainer: {
      [theme.breakpoints.down('xs')]: {
        fontSize: theme.typography.body1.fontSize,
        paddingRight: theme.spacing(1),
      },
    },
    attribute: {
      color: GRAPHITE,
      fontSize: '0.875rem',
      marginTop: theme.spacing(0.5),
      fontWeight: 'normal',
      [theme.breakpoints.down('xs')]: {
        paddingBottom: 0,
        fontSize: '0.8rem',
      },
    },
    premium: {
      color: DARK,
      fontSize: '1rem',
      lineHeight: 1,
      [theme.breakpoints.down('xs')]: {
        float: 'right',
      },
    },
    discountStamp: {
      position: 'absolute',
      right: 0,
      top: 0,
      padding: '0px 6px',
      color: WHITE,
      backgroundColor: ROSE,
      borderBottomLeftRadius: theme.shape.borderRadius * 2,
      borderTopRightRadius: theme.shape.borderRadius * 2,
      borderWidth: 0,
      fontWeight: 500,
      fontSize: 14,
      lineHeight: '150%',
    },
    dollarSign: {
      fontSize: '1rem',
      verticalAlign: 16,
      [theme.breakpoints.down('xs')]: {
        fontSize: '0.75rem',
        verticalAlign: 8,
      },
    },
    productDetailContainer: {
      marginBottom: theme.spacing(2),
      [theme.breakpoints.down('xs')]: {
        marginBottom: theme.spacing(0),
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
      },
    },
    premiumBeforeDiscount: {
      color: GRAPHITE,
      fontSize: '0.75rem',
      textDecoration: 'line-through',
      [theme.breakpoints.up('sm')]: {
        fontSize: '1rem',
      },
    },
    price: {
      color: ({ discounted }) => (discounted ? ROSE : DARK),
      fontSize: '2.5rem',
      fontWeight: 700,
      [theme.breakpoints.down('xs')]: {
        fontSize: '1.5rem',
      },
    },
    unit: {
      color: GRAPHITE,
      [theme.breakpoints.down('xs')]: {
        fontSize: '0.75rem',
      },
    },
  }),
);

const ProductCard = ({ product, selected, onClick }: Props) => {
  const { values, validateIssueAge, isSumAssuredValid, coupon } =
    useQuotationContext();

  const handleClick = useCallback(() => {
    onClick(product);
  }, [onClick, product]);

  const spec = useMemo(() => {
    return EXTRA_PRODUCT_SPECS_BY_CODE[product.code];
  }, [product]);

  const [quotation] = useQuotation({
    productId: product.code,
    age: values.age,
    gender: values.gender,
    smoker: values.smoker,
    sumAssured: spec?.sumAssured ? values.sumAssured : 0,
    couponCode: coupon?.promo_code,
  });
  const classes = useStyles({ selected, discounted: !!quotation?.discount });

  const countUp = useNumberCountUp(
    quotation && quotation.discount > 0
      ? quotation.pre_levy_premium
      : quotation?.basic_premium,
  );

  const isIssueAgeValid = validateIssueAge(product, values.age);

  const discountOffString = useMemo(() => {
    if (quotation?.discount_config) {
      const { amount_off, percent_off, discount_type } =
        quotation.discount_config;
      const isPercentOff = discount_type === 'percent_off';
      const offString = isPercentOff
        ? `-${Number(percent_off)}%`
        : `-$${Number(amount_off)}`;

      return offString;
    }
    return '';
  }, [quotation?.discount_config]);

  return (
    <Box position="relative">
      <Grid
        className={clsx(classes.card, classes.activeOpacity)}
        onClick={handleClick}
      >
        <Grid container className={classes.container}>
          <Grid item xs={7} sm={12} className={classes.productDetailContainer}>
            <Hidden xsDown>
              <Typography
                variant="subtitle1"
                style={{
                  color: spec.headlineColor,
                  paddingBottom: '4px',
                }}
              >
                {product.headline}
              </Typography>
            </Hidden>
            <Typography variant="h6" className={classes.productNameContainer}>
              {product.short_name}
            </Typography>
            {product.annual_limit && (
              <Typography className={classes.attribute}>
                <FormattedMessage
                  id="coverage.annualLimit"
                  values={{
                    annualLimit: product.annual_limit.toLocaleString(),
                  }}
                />
              </Typography>
            )}
            {product.lifetime_limit && (
              <Typography className={classes.attribute}>
                <FormattedMessage
                  id="coverage.lifetimeLimit"
                  values={{
                    lifetimeLimit: product.lifetime_limit.toLocaleString(),
                  }}
                />
              </Typography>
            )}
          </Grid>
          {isIssueAgeValid && isSumAssuredValid && (
            <Grid item xs={5} sm={12} className={classes.priceContainer}>
              <Box>
                {!!quotation && quotation.discount > 0 && (
                  <Typography
                    className={classes.premiumBeforeDiscount}
                  >{`$${Math.round(quotation.basic_premium)}`}</Typography>
                )}
                <Typography className={classes.premium}>
                  <span className={classes.dollarSign}>$</span>
                  <span className={classes.price}>{countUp}</span>
                  <span className={classes.unit}>
                    /<FormattedMessage id="month" />
                  </span>
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      </Grid>
      {!!quotation && quotation.discount > 0 && (
        <Box className={clsx(classes.activeOpacity, classes.discountStamp)}>
          {discountOffString}
        </Box>
      )}
    </Box>
  );
};

export default ProductCard;
